import React, {useEffect, useState} from "react";
import intl from "react-intl-universal";

import {MuiIcon} from "../icons/MuiIcon";
import {ChevronRightIcon} from "../icons/ChevronRightIcon";
import {InfoIcon} from "../icons/InfoIcon";
import {CheckIcon} from "../icons/CheckIcon";
import {NotificationCircleIcon} from "../icons/NotificationCircleIcon";
import {Button} from "../Button/Button";

import "./TaskOverdueDetailsCard.css";

export interface TaskOverdueOccurrence {
    date: Date;
    daysOverdue: number;
    onClick?: () => void;
}

export interface TaskOverdueRemindAction {
    label: string;
    /** Shown on the button in place of `label` for a couple seconds after it's
     *  clicked, so the click has visible feedback even though nothing else on
     *  the card changes. */
    sentLabel?: string;
    onClick: () => void;
}

export interface TaskOverdueDetailsCardProps {
    occurrences: TaskOverdueOccurrence[];
    /** Overrides the header label - defaults to "Overdue history", which reads
     *  oddly for a caller (e.g. a single audit assignment's due date) that has
     *  only ever been overdue once and isn't reporting a history of misses. */
    title?: string;
    /** Overrides the read-only footer hint (shown when no occurrence has an
     *  onClick) for callers whose "still open" action isn't "complete it". */
    footerHintReadonly?: string;
    /** Adds a "Remind" button below the footer hint - for a caller whose overdue
     *  item has one owner who can be nudged (e.g. an audit assignee), unlike a
     *  recurring task's history of dates where "remind" has no single target. */
    remindAction?: TaskOverdueRemindAction;
}

export const TaskOverdueDetailsCard = ({occurrences, title, footerHintReadonly, remindAction}: TaskOverdueDetailsCardProps) => {
    const [justSent, setJustSent] = useState(false);
    useEffect(() => {
        if (!justSent) return;
        const timer = window.setTimeout(() => setJustSent(false), 2000);
        return () => window.clearTimeout(timer);
    }, [justSent]);

    const hasClickableOccurrences = occurrences.some((occurrence) => occurrence.onClick);
    // A single read-only occurrence is one audit's one due date, not a history of
    // misses - it reads better as a compact "due X, N days late" summary than as
    // a one-row list styled for scanning several dates.
    const isSingleSummary = occurrences.length === 1 && !hasClickableOccurrences;
    const footerHint = hasClickableOccurrences
        ? intl.get('TaskOverdue.footerHint').d('Click a date to complete it')
        : footerHintReadonly ?? intl.get('TaskOverdue.footerHintReadonly').d('Still open — can be completed late');

    return (
        <div className="TaskOverdueDetailsCard">
            {/* The single-occurrence summary (audit case) already says "overdue" via
                its date + red pill, so it skips this red banner entirely - it's
                redundant there and heavier than the compact card needs. */}
            {!isSingleSummary && (
                <div className="TaskOverdueDetailsCard__header">
                    <span className="TaskOverdueDetailsCard__headerIconWrap">
                        <MuiIcon code="schedule" size={16} className="TaskOverdueDetailsCard__headerIcon"/>
                        {occurrences.length > 1 && (
                            <span className="TaskOverdueDetailsCard__headerBadge">{occurrences.length}</span>
                        )}
                    </span>
                    <span className="TaskOverdueDetailsCard__headerText title-medium">
                        {title ?? intl.get('TaskOverdue.title').d('Overdue history')}
                    </span>
                </div>
            )}
            {isSingleSummary ? (
                <div className="TaskOverdueDetailsCard__summary">
                    <div className="TaskOverdueDetailsCard__summaryLabel label-small">
                        {intl.get('TaskOverdue.dueDateLabel').d('Was due')}
                    </div>
                    <div className="TaskOverdueDetailsCard__summaryRow">
                        <span className="TaskOverdueDetailsCard__summaryDate body-medium">
                            {occurrences[0].date.toLocaleDateString(intl.getInitOptions().currentLocale ?? undefined, {day: 'numeric', month: 'long', year: 'numeric'})}
                        </span>
                        <span className="TaskOverdueDetailsCard__summaryPill label-small">
                            {intl.get('TaskOverdue.daysLate', {count: occurrences[0].daysOverdue}).d('Overdue by {count} d.')}
                        </span>
                    </div>
                </div>
            ) : (
                <div className="TaskOverdueDetailsCard__list">
                    {occurrences.map((occurrence, i) => (
                        <div
                            key={i}
                            className={`TaskOverdueDetailsCard__row${occurrence.onClick ? ' TaskOverdueDetailsCard__row--clickable' : ''}`}
                            onClick={occurrence.onClick}
                        >
                            <MuiIcon code="schedule" size={16} className="TaskOverdueDetailsCard__rowIcon"/>
                            <div className="TaskOverdueDetailsCard__rowText">
                                <div className="body-medium">{occurrence.date.toLocaleDateString(intl.getInitOptions().currentLocale ?? undefined, {day: 'numeric', month: 'long', year: 'numeric'})}</div>
                                <div className="TaskOverdueDetailsCard__rowOverdue label-small">
                                    {intl.get('TaskOverdue.daysLate', {count: occurrence.daysOverdue}).d('Overdue by {count} d.')}
                                </div>
                            </div>
                            {occurrence.onClick && (
                                <ChevronRightIcon size={16} className="TaskOverdueDetailsCard__rowChevron"/>
                            )}
                        </div>
                    ))}
                </div>
            )}
            {remindAction && (
                <div className="TaskOverdueDetailsCard__remindWrap">
                    <Button
                        className="TaskOverdueDetailsCard__remindButton"
                        style="outlined"
                        type="primary"
                        size="sm"
                        fullWidth
                        isDisable={justSent}
                        Icon={justSent ? CheckIcon : NotificationCircleIcon}
                        text={justSent ? (remindAction.sentLabel ?? intl.get('TaskOverdue.reminderSent').d('Reminder sent')) : remindAction.label}
                        onClick={() => {
                            remindAction.onClick();
                            setJustSent(true);
                        }}
                    />
                </div>
            )}
            {/* The single-occurrence summary (audit case) already shows the one
                thing this hint would say - that it's still open - via its own
                status column, so the hint is redundant there and just skipped. */}
            {!isSingleSummary && (
                <div className="TaskOverdueDetailsCard__footer">
                    <InfoIcon size={14} className="TaskOverdueDetailsCard__footerIcon"/>
                    <span className="label-small">{footerHint}</span>
                </div>
            )}
        </div>
    );
};

export default TaskOverdueDetailsCard;
